import { Box, useMantineTheme } from '@mantine/core';
import { useState } from 'react';

import { NetworkIcon } from './NetworkIcon';
import { resolveWhopIconUrl } from './resolveWhopIconUrl';

// Fixed size so every row (icon or letter-avatar fallback) reports the same height.
const ICON_SIZE = 32;
const BADGE_SIZE = 16;
const BADGE_BORDER = 1.5;
// The network icon fills the badge's inner (content) box, so it's concentric with the badge circle
// without changing the badge's overall 16px footprint (EDGE-32115).
const BADGE_INNER_SIZE = BADGE_SIZE - BADGE_BORDER * 2;

type AssetIconProps = {
  symbol: string;
  iconUrl: string | null;
  networkName: string;
  networkIconUrl: string | null;
};

/** An asset's icon, badged with its network's icon in the corner. Falls back to a letter
 * avatar when there's no iconUrl or the <img> fails to load — mirrors NetworkIcon. */
export function AssetIcon({ symbol, iconUrl, networkName, networkIconUrl }: AssetIconProps) {
  const theme = useMantineTheme();
  const [errored, setErrored] = useState(false);
  const letter = symbol.charAt(0).toUpperCase();

  return (
    <Box sx={{ position: 'relative', width: ICON_SIZE, height: ICON_SIZE, minWidth: ICON_SIZE }}>
      {!iconUrl || errored ? (
        <Box
          sx={{
            width: ICON_SIZE,
            height: ICON_SIZE,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: theme.colors.gray2[1],
            color: theme.colors.gray2[7],
            fontWeight: 600,
          }}
        >
          {letter}
        </Box>
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={resolveWhopIconUrl(iconUrl)}
          alt=""
          width={ICON_SIZE}
          height={ICON_SIZE}
          style={{
            width: ICON_SIZE,
            height: ICON_SIZE,
            borderRadius: '50%',
            objectFit: 'cover',
          }}
          onError={() => setErrored(true)}
        />
      )}
      {/* EDGE-32115: render the network icon at the badge's INNER size (BADGE_INNER_SIZE) so it fills the
          badge's content box and is concentric with the badge circle, keeping the 16px footprint. The app
          applies an ambient `*{box-sizing:border-box}` reset (theme.ts globalStyles, which MantineProvider
          renders regardless of `withGlobalStyles`), so the content box is 13px here; the explicit
          `boxSizing` is belt-and-suspenders — redundant given that reset, kept so the badge stays correct
          if it ever changes. Do NOT set `lineHeight: 0` here: it is inherited into the NetworkIcon fallback
          letter box and collapses its line box, defeating the flex vertical-centring (the placeholder
          letter renders low). The old `transform: scale()` + `transformOrigin: 'top left'` shrank a 32px
          icon and anchored it to the border-inset content origin, off-centring it. */}
      <Box
        sx={{
          position: 'absolute',
          bottom: -2,
          right: -2,
          boxSizing: 'border-box',
          width: BADGE_SIZE,
          height: BADGE_SIZE,
          borderRadius: '50%',
          border: `${BADGE_BORDER}px solid ${theme.white}`,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <NetworkIcon name={networkName} iconUrl={networkIconUrl} size={BADGE_INNER_SIZE} />
      </Box>
    </Box>
  );
}
